/**
 * **「孩子看到的今天」只读预览**（2026-10-31 用户要求，第二版）。
 *
 * ## 用户原话
 *
 * > "家长给孩子设了目标，还有目标下面的打卡任务。但需要去看孩子具体能看到什么时，
 * >  必须切换到孩子的账号去看……能不能找个功能入口，让显示的界面跟孩子端看到的一样。
 * >  但是，点进去后，不能代替孩子打卡。"
 *
 * 第二版的两点修正（同一轮反馈）：
 *
 * > "上面好像有些内容看不到？还有这个界面的样式能不能和小孩端的一样？"
 *
 *  1. **不再是弹窗**：弹窗在手机上比屏幕还高，标题被顶到屏幕外（截图里那一行只剩
 *     半截）。现在是一层**全屏浮层**（自带返回栏 + 可滚动），和"进入一个页面"一样；
 *  2. **卡片样式照抄孩子端**：图标块 + 名字 + 状态徽章 + 「属于 / 目标 / 预计 / 可得」，
 *     儿童模式用大一号的字号与 2px 边框 —— 目标只有一个：**看起来就是孩子看到的那个界面**。
 *
 * ## 它不是什么
 *
 * **没有任何写入口**：没有「开始 / 完成 / 放弃」，计时面板固定 `live={false}`
 * （不推进、不发心跳、不响提示音）。"看"不能变成"替他打卡"。
 */
import { useMemo, useState } from 'react';
import { Badge, EmptyState, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { CheckinTimerPanel } from '../ui/CheckinTimerPanel';
import { ErrorState, iconForName, statusMeta } from '../ui/Bits';
import { useToday } from '../../lib/domainQueries';
import { uniqueTodayItems } from '../../lib/todayGroups';
import { toErrorMessage } from '../../lib/api/error';
import { cn, formatPlannedDuration } from '../../lib/utils';
import type { TodayTaskDetail } from '../../lib/api/types';

export interface ChildTodayPreviewProps {
  open: boolean;
  studentId: string;
  studentName?: string;
  onClose: () => void;
}

export function ChildTodayPreview({ open, studentId, studentName, onClose }: ChildTodayPreviewProps) {
  /**
   * ⚠️ 关着的时候**不发请求**（`studentId` 传 null）：家长只是路过这一页，
   * 不该为了一个没打开的预览多打一次接口。
   */
  const query = useToday(open ? studentId : null);
  const data = query.data;

  const items = useMemo(() => uniqueTodayItems(data?.items ?? []), [data]);
  const pending = useMemo(() => items.filter((task) => task.status !== 'done'), [items]);
  const done = useMemo(() => items.filter((task) => task.status === 'done'), [items]);
  const [showDone, setShowDone] = useState(false);

  const kid = data?.uiMode === 'kid';

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-50">
      {/* ---------- 顶部：返回 + 标题（固定，不会像弹窗那样被顶出屏幕） ---------- */}
      <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <Button size="sm" variant="ghost" onClick={onClose}>
          ← 返回
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">
            {studentName ? `${studentName} 看到的「今天」` : '孩子看到的「今天」'}
          </p>
          <p className="truncate text-[11px] text-slate-500">只读预览 · 不能代替孩子打卡</p>
        </div>
        {/* 孩子端右上角那颗 ⭐ 积分（预览里也放，样子一致；不加任何操作）。 */}
        {typeof data?.pointsBalance === 'number' && (
          <span
            className="shrink-0 rounded-xl bg-amber-50 px-2 py-1 text-sm font-bold text-amber-700"
            aria-label={`当前积分 ${data.pointsBalance}`}
          >
            ⭐ {data.pointsBalance}
          </span>
        )}
      </header>

      {/* ---------- 正文（可滚动） ---------- */}
      <div className={cn('min-h-0 flex-1 overflow-y-auto px-3 py-3', kid && 'ui-child')}>
        <div className="mx-auto max-w-2xl space-y-3">
          {/*
            ---------- 今日进度（**和孩子端同一块**） ----------
            用户反馈："那个样式还是差一点意思，就好像底色没有加上去是吧？"
            —— 缺的正是这块蓝色渐变卡：孩子端一进页面最显眼的就是它。
          */}
          {items.length > 0 && (
            <section
              aria-label="今日进度"
              className={cn(
                'rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white',
                kid ? 'p-5' : 'p-4',
              )}
            >
              <p className={cn('text-brand-50', kid ? 'text-lg' : 'text-sm')}>今天完成了</p>
              <p className={cn('font-bold tabular-nums', kid ? 'text-5xl' : 'text-3xl')}>
                {done.length}
                <span className={cn('ml-1 font-medium', kid ? 'text-2xl' : 'text-lg')}>
                  / {items.length} 项
                </span>
              </p>
              <p className={cn('mt-2 text-brand-50', kid ? 'text-base' : 'text-xs')}>
                {pending.length > 0
                  ? `还有 ${pending.length} 项没做，一件一件来就好`
                  : '今天的任务全部完成，太棒了！'}
              </p>
            </section>
          )}

          <div className="rounded-xl border border-brand-200 bg-brand-50 px-3 py-2">
            <p className="text-sm font-medium text-brand-900">不能代替孩子打卡</p>
            <p className="mt-0.5 text-xs text-brand-800">
              开始、完成、放弃都只能在孩子端操作；这里只是让你看看他面对的是什么。
            </p>
          </div>

          {query.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : query.isError ? (
            <ErrorState message={toErrorMessage(query.error)} onRetry={() => void query.refetch()} />
          ) : data?.notReady === true ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              打卡接口还没上线，暂时看不到孩子的清单。
            </p>
          ) : items.length === 0 ? (
            <EmptyState
              icon="🌱"
              title="孩子今天没有要打的卡"
              description="可能是今天不在计划内，也可能是目标都暂停/归档了 —— 孩子端看到的也是这个结果。"
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>
                  共 {items.length} 项 · 还没做 {pending.length} 项 · 已完成 {done.length} 项
                </span>
                {data?.localDate !== '' && <Badge tone="neutral">{data?.localDate}</Badge>}
                {kid && <Badge tone="brand">儿童模式</Badge>}
              </div>

              {done.length > 0 && (
                <div className="space-y-2">
                  <button
                    type="button"
                    aria-expanded={showDone}
                    onClick={() => setShowDone((value) => !value)}
                    className="flex w-full items-center justify-between rounded-xl bg-emerald-50 px-3 py-2 text-left"
                  >
                    <span className="text-sm font-medium text-emerald-800">
                      ✅ 已完成 {done.length} 项{showDone ? '' : '（点这里看）'}
                    </span>
                    <span className="text-xs text-emerald-700">
                      {showDone ? '收起 ▲' : '展开 ▼'}
                    </span>
                  </button>
                  {showDone && (
                    <ul className="space-y-3">
                      {done.map((task) => (
                        <PreviewRow key={task.checkinId} task={task} kid={kid} />
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <ul className="space-y-3">
                {pending.map((task) => (
                  <PreviewRow key={task.checkinId} task={task} kid={kid} />
                ))}
              </ul>

              {/* 儿童模式的"小星星"也算孩子看到的一部分（对齐孩子端）。 */}
              {kid && (
                <section className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-lg font-medium text-slate-700">我的小星星</p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {items.map((task) => (
                      <span
                        key={task.checkinId}
                        aria-hidden="true"
                        title={task.name}
                        className={cn(
                          'flex h-14 w-14 items-center justify-center rounded-2xl text-3xl',
                          task.status === 'done' ? 'bg-amber-100' : 'bg-slate-100 opacity-40',
                        )}
                      >
                        {task.status === 'done' ? '⭐' : '☆'}
                      </span>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 一张卡（**只读**），视觉照抄孩子端 `TaskRow`：图标块 + 名字 + 状态 + 说明行 + 计时面板。
 *
 * ⚠️ 刻意**不渲染任何按钮** —— 见文件头：预览不能变成"替孩子打卡"。
 */
function PreviewRow({ task, kid }: { task: TodayTaskDetail; kid: boolean }) {
  const meta = statusMeta(task.status);
  const isDone = task.status === 'done';
  const running =
    task.status === 'running' || task.status === 'paused' || task.status === 'auto_paused';

  return (
    <li>
      <div
        className={cn(
          'rounded-2xl border bg-white',
          kid ? 'border-2 p-4' : 'border-slate-200 p-3',
          running && 'border-brand-400 ring-2 ring-brand-100',
          isDone && 'bg-emerald-50/60',
        )}
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className={cn(
              'flex shrink-0 items-center justify-center rounded-2xl bg-brand-50',
              kid ? 'h-14 w-14 text-3xl' : 'h-11 w-11 text-xl',
            )}
          >
            {iconForName(task.name, isDone ? '✅' : '📘')}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className={cn('font-semibold text-slate-900', kid ? 'text-xl' : 'text-base')}>
                {task.name}
              </p>
              <Badge tone={meta.tone}>
                {meta.icon} {meta.label}
              </Badge>
              {task.isMakeup && <Badge tone="warn">补卡</Badge>}
            </div>

            <p className={cn('mt-1 text-slate-500', kid ? 'text-base' : 'text-xs')}>
              {[
                task.goalName ? `属于：${task.goalName}` : null,
                task.targetValue ? `目标 ${task.targetValue}${task.unit ?? ''}` : null,
                task.estimatedSeconds
                  ? `预计 ${formatPlannedDuration(task.estimatedSeconds)}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>

            {running && (
              <CheckinTimerPanel
                checkinId={task.checkinId}
                status={task.status}
                actualSeconds={task.actualSeconds ?? 0}
                estimatedSeconds={task.estimatedSeconds ?? null}
                kid={kid}
                tone="brand"
                /* 家长端只读：不推进、不发心跳、不响提示音（见文件头）。 */
                live={false}
              />
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
